import { type Stats, type SetPlayerStatReducerProps, type StatsDispatchProps, type SetEquippedStatsReducerProps } from 'src/types'
import { defaultStats } from './defaults'

const setPlayerStat = (stats: Stats, { statID, statEffect }: SetPlayerStatReducerProps) => {
  const [key, value] = Object.entries(statEffect)[0]

  // this checks if the value is sourced directly or from an existing variable

  const count: number = typeof value === 'number' ? value : Number(value) ? Number(value) : stats.player[value as keyof typeof stats.player] || 0
  return {
    ...stats,
    player: {
      ...stats.player,
      [statID]: (
        key === '='
          ? count
          : key === '+'
            ? (stats.player[statID] || 0) + count
            : key === '-' && (stats.player[statID] || 0) - count < 0 && statID !== 'player_karma'
              ? 0
              : key === '-'
                ? (stats.player[statID] || 0) - count
                : stats.player[statID]
      )
    }
  }
}

const setEquippedStats = (stats: Stats, { items, inventory }: SetEquippedStatsReducerProps) => {
  const newEquipmentStats: { [key: string]: number } = {}

  Object.entries(inventory.equipped).forEach(([, itemID]) => {
    if (itemID.length && items[itemID] && items[itemID].effects) {
      const itemEffects = items[itemID].effects
      if (itemEffects && itemEffects.counters) {
        Object.entries(itemEffects.counters).forEach(([effectID, effect]) => {
          const [operator, effectValue] = Object.entries(effect)[0]
          let value = 0
          if (typeof effectValue === 'number') {
            value = effectValue
          } else if (typeof effectValue === 'string' && !isNaN(Number(effectValue))) {
            value = Number(effectValue)
          } else {
            // TODO: handle variables as effectValues
            throw new Error('items should not use variables as effects')
          }

          if (operator === '-') {
            newEquipmentStats[effectID] = (newEquipmentStats[effectID] || 0) - value
          } else {
            newEquipmentStats[effectID] = (newEquipmentStats[effectID] || 0) + value
          }
        })
      }
    }
  })

  const bagItemsWeight = Object.entries(inventory.bag).reduce((total, [itemID, quantity]) => (total + (quantity * items[itemID].weight || 0)), 0)
  const equippedWeight = Object.entries(inventory.equipped).reduce((total, [, itemID]) => (total + (items[itemID] && items[itemID].weight ? items[itemID].weight : 0)), 0)
  newEquipmentStats.weight = bagItemsWeight + equippedWeight

  const defaultStats = {
    bag_slots: 2,
    bag_slots_max: 20,
    kg: 0,
    kg_max: 10,
    attack_bonus: 0,
    armor_bonus: 0,
    player_str: 0,
    player_dex: 0,
    player_vit: 0,
    player_hp_max: 0,
    player_ap_max: 0,
    light_radius: 0
  }

  const newStats: Stats = {
    ...stats,
    equipped: {
      ...defaultStats,
      ...newEquipmentStats
    }
  }

  return newStats
}


const updateTotalStats = (stats: Stats) => {
  const total = {...stats.player, ...stats.equipped}
  Object.entries(total).forEach(([key]) => {
    total[key as keyof typeof total] = (stats.player[key as keyof typeof stats.player] || 0) + (stats.equipped[key as keyof typeof stats.equipped] || 0)
  })

  total.kg_max = total.kg_max + stats.player.player_vit * 5 + stats.equipped.player_vit * 5
  
  return {
    ...stats,
    total
  }
}

const resetStats = () => defaultStats

export const reducer = (stats: Stats, action: StatsDispatchProps) => {
  switch (action.type) {
    case 'SET_PLAYER_STAT':
      return setPlayerStat(stats, action)
    case 'SET_EQUIPMENT_STATS':
      return setEquippedStats(stats, action)
    case 'UPDATE_TOTAL_STATS':
      return updateTotalStats(stats)
    case 'RESET_STATS':
      return resetStats()
    default:
      return stats
  }
}
